if ( window.wp && window.wp.ajax ) {

    const { registerBlockType } = window.wp.blocks;

    const {
        InspectorControls,
        BlockControls
    } = window.wp.editor;

    const {
        PanelBody,
        TextControl,
        RangeControl,
        SelectControl,
        ToggleControl,
        Notice
    } = window.wp.components;

    const { Fragment } = window.wp.element;

    /**
     * Extract YouTube video ID from various URL formats:
     *   https://www.youtube.com/watch?v=VIDEO_ID
     *   https://youtu.be/VIDEO_ID
     *   https://www.youtube.com/embed/VIDEO_ID
     *   https://youtube.com/shorts/VIDEO_ID
     */
    function getYouTubeId(url) {
        if (!url) return null;
        const patterns = [
            /(?:youtube\.com\/watch\?(?:.*&)?v=)([^&#?]+)/,
            /(?:youtu\.be\/)([^&#?]+)/,
            /(?:youtube\.com\/embed\/)([^&#?]+)/,
            /(?:youtube\.com\/shorts\/)([^&#?]+)/,
        ];
        for (const pattern of patterns) {
            const match = url.match(pattern);
            if (match) return match[1];
        }
        return null;
    }

    function buildEmbedUrl(videoId, { autoplay, loop, controls, mute, startTime }) {
        const params = new URLSearchParams();
        if (autoplay)  params.set('autoplay', '1');
        if (loop)      { params.set('loop', '1'); params.set('playlist', videoId); }
        if (!controls) params.set('controls', '0');
        if (mute)      params.set('mute', '1');
        if (startTime) params.set('start', String(startTime));
        params.set('rel', '0');
        const query = params.toString();
        return `https://www.youtube.com/embed/${videoId}${query ? '?' + query : ''}`;
    }

    registerBlockType('custom-gutenberg/video', {
        title: 'Custom Video',
        description: 'Embed a YouTube video with custom dimensions and playback options',
        icon: 'video-alt3',
        category: 'custom-components',
        example: {},

        attributes: {
            url: {
                type: 'string',
                default: '',
            },
            widthUnit: {
                type: 'string',
                default: 'percent',   // 'percent' | 'px'
            },
            widthPercent: {
                type: 'number',
                default: 100,
            },
            widthPx: {
                type: 'number',
                default: 640,
            },
            height: {
                type: 'number',
                default: 360,
            },
            autoplay: {
                type: 'boolean',
                default: false,
            },
            loop: {
                type: 'boolean',
                default: false,
            },
            controls: {
                type: 'boolean',
                default: true,
            },
            mute: {
                type: 'boolean',
                default: false,
            },
            startTime: {
                type: 'number',
                default: 0,
            },
            align: {
                type: 'string',
                default: 'center',    // 'left' | 'center' | 'right'
            },
        },

        edit({ attributes, setAttributes }) {
            const {
                url, widthUnit, widthPercent, widthPx, height,
                autoplay, loop, controls, mute, startTime, align,
            } = attributes;

            const videoId  = getYouTubeId(url);
            const embedUrl = videoId ? buildEmbedUrl(videoId, { autoplay, loop, controls, mute, startTime }) : null;

            const widthStyle = widthUnit === 'percent'
                ? `${widthPercent}%`
                : `${widthPx}px`;

            const containerStyle = {
                textAlign: align,
            };

            const wrapperStyle = {
                display: 'inline-block',
                width: widthStyle,
                maxWidth: '100%',
            };

            return (
                <Fragment>
                    <InspectorControls>
                        <PanelBody title="YouTube URL" initialOpen={true}>
                            <TextControl
                                label="Video URL"
                                value={url}
                                onChange={(val) => setAttributes({ url: val })}
                                placeholder="https://www.youtube.com/watch?v=..."
                                help="Paste enter any YouTube URL."
                            />
                            {url && !videoId && (
                                <Notice status="error" isDismissible={false}>
                                    Could not detect a YouTube video ID. Please check the URL.
                                </Notice>
                            )}
                            {videoId && (
                                <Notice status="success" isDismissible={false}>
                                    Video ID: <strong>{videoId}</strong>
                                </Notice>
                            )}
                        </PanelBody>
                        <PanelBody title="Dimensions" initialOpen={true}>
                            <SelectControl
                                label="Width unit"
                                value={widthUnit}
                                options={[
                                    { label: 'Percent (%)', value: 'percent' },
                                    { label: 'Pixels (px)',  value: 'px'      },
                                ]}
                                onChange={(val) => setAttributes({ widthUnit: val })}
                            />
                            {widthUnit === 'percent' ? (
                                <RangeControl
                                    label={`Width: ${widthPercent}%`}
                                    value={widthPercent}
                                    onChange={(val) => setAttributes({ widthPercent: val })}
                                    min={10}
                                    max={100}
                                    step={1}
                                />
                            ) : (
                                <TextControl
                                    label="Width (px)"
                                    type="number"
                                    value={String(widthPx)}
                                    onChange={(val) => setAttributes({ widthPx: parseInt(val, 10) || 640 })}
                                    min={100}
                                />
                            )}
                            <TextControl
                                label="Height (px)"
                                type="number"
                                value={String(height)}
                                onChange={(val) => setAttributes({ height: parseInt(val, 10) || 360 })}
                                min={100}
                            />
                        </PanelBody>
                        <PanelBody title="Alignment" initialOpen={false}>
                            <SelectControl
                                label="Horizontal alignment"
                                value={align}
                                options={[
                                    { label: 'Left',   value: 'left'   },
                                    { label: 'Center', value: 'center' },
                                    { label: 'Right',  value: 'right'  },
                                ]}
                                onChange={(val) => setAttributes({ align: val })}
                            />
                        </PanelBody>
                        <PanelBody title="Playback Options" initialOpen={false}>
                            <ToggleControl
                                label="Autoplay"
                                checked={autoplay}
                                onChange={(val) => setAttributes({ autoplay: val })}
                                help="Autoplay requires Mute to work in most browsers."
                            />
                            <ToggleControl
                                label="Loop"
                                checked={loop}
                                onChange={(val) => setAttributes({ loop: val })}
                            />
                            <ToggleControl
                                label="Show player controls"
                                checked={controls}
                                onChange={(val) => setAttributes({ controls: val })}
                            />
                            <ToggleControl
                                label="Mute"
                                checked={mute}
                                onChange={(val) => setAttributes({ mute: val })}
                            />
                            <TextControl
                                label="Start time (seconds)"
                                type="number"
                                value={String(startTime)}
                                onChange={(val) => setAttributes({ startTime: parseInt(val, 10) || 0 })}
                                min={0}
                                help="Start playback at this many seconds into the video."
                            />
                        </PanelBody>
                    </InspectorControls>,

                    <div style={containerStyle}>
                        {embedUrl ? (
                            <div style={wrapperStyle}>
                                <iframe
                                    src={embedUrl}
                                    width="100%"
                                    height={height}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    title="YouTube video"
                                    style={{ display: 'block' }}
                                />
                            </div>
                        ) : (
                            <div style={{
                                padding: '40px 20px',
                                background: '#f0f0f0',
                                border: '2px dashed #ccc',
                                borderRadius: '4px',
                                textAlign: 'center',
                            }}>
                                <span
                                    className="dashicons dashicons-video-alt3"
                                    style={{ fontSize: '48px', color: '#aaa', display: 'block', marginBottom: '12px' }}
                                />
                                <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                                    Paste a YouTube URL in the block settings (sidebar) to preview the video.
                                </p>
                            </div>
                        )}
                    </div>
                </Fragment>
            );
        },

        save({ attributes }) {
            const {
                url, widthUnit, widthPercent, widthPx, height,
                autoplay, loop, controls, mute, startTime, align,
            } = attributes;

            const videoId = getYouTubeId(url);
            if (!videoId) return null;

            const embedUrl  = buildEmbedUrl(videoId, { autoplay, loop, controls, mute, startTime });
            const widthStyle = widthUnit === 'percent' ? `${widthPercent}%` : `${widthPx}px`;

            return (
                <div
                    className="wp-block-custom-gutenberg-youtube"
                    style={{ textAlign: align }}
                >
                    <div style={{ display: 'inline-block', width: widthStyle, maxWidth: '100%' }}>
                        <iframe
                            src={embedUrl}
                            width="100%"
                            height={height}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            title="YouTube video"
                            style={{ display: 'block' }}
                        />
                    </div>
                </div>
            );
        },
    });
};